import { useAppDispatch } from '../../redux/hooks';
import { useMutation } from '@apollo/client';
import {
  IAnalysisDeleteSummaryRichTextMutation,
  IAnalysisDeleteSummaryRichTextMutationVariables,
  IAnalysisSetShowSummaryInDashboardMutation,
  IAnalysisSetShowSummaryInDashboardMutationVariables,
  IAnalysisSetSummaryRichTextMutation,
  IAnalysisSetSummaryRichTextMutationVariables,
} from '../apollo/graphqlSchema';
import {
  ANALYSIS_DELETE_SUMMARY_RICH_TEXT,
  ANALYSIS_SET_SHOW_SUMMARY_IN_DASHBOARD,
  ANALYSIS_SET_SUMMARY_RICH_TEXT,
} from '../apollo/analysis';
import { useCallback } from 'react';
import printGraphqlErrors from '../apollo/printGraphqlErrors';
import {
  updateAnalysisSummaryRichText,
  updateAnalysisSummaryShowInDashboard,
} from '../../redux/slices/analyses';
import toastMessage from '../toastMessage';

interface IProps {
  analysisId: string;
}

const useAnalysisSaveSummaryRichText = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();

  const [analysisSetSummaryRichText, { loading: savingRichText }] = useMutation<
    IAnalysisSetSummaryRichTextMutation,
    IAnalysisSetSummaryRichTextMutationVariables
  >(ANALYSIS_SET_SUMMARY_RICH_TEXT);

  const [analysisDeleteSummaryRichText, { loading: deletingRichText }] =
    useMutation<
      IAnalysisDeleteSummaryRichTextMutation,
      IAnalysisDeleteSummaryRichTextMutationVariables
    >(ANALYSIS_DELETE_SUMMARY_RICH_TEXT);

  const [
    analysisSetShowSummaryInDashboard,
    { loading: savingShowInDashboard },
  ] = useMutation<
    IAnalysisSetShowSummaryInDashboardMutation,
    IAnalysisSetShowSummaryInDashboardMutationVariables
  >(ANALYSIS_SET_SHOW_SUMMARY_IN_DASHBOARD);

  const saveSummaryRichText = useCallback(
    async (richText: string) => {
      const resp = await analysisSetSummaryRichText({
        variables: {
          input: {
            analysisId,
            richText,
          },
        },
      });
      if (printGraphqlErrors(resp.data?.analysisSetSummaryRichText.errors)) {
        return;
      }
      dispatch(
        updateAnalysisSummaryRichText({
          analysisId,
          richText,
        }),
      );
      const message = resp.data?.analysisSetSummaryRichText?.entity;
      if (message) {
        toastMessage({ message, type: 'info' });
      }
    },
    [analysisSetSummaryRichText, analysisId, dispatch],
  );

  const deleteSummaryRichText = useCallback(async () => {
    const resp = await analysisDeleteSummaryRichText({
      variables: {
        input: {
          analysisId,
        },
      },
    });
    if (printGraphqlErrors(resp.data?.analysisDeleteSummaryRichText.errors)) {
      return;
    }
    dispatch(
      updateAnalysisSummaryRichText({
        analysisId,
        richText: null,
      }),
    );
    const message = resp.data?.analysisDeleteSummaryRichText?.entity;
    if (message) {
      toastMessage({ message, type: 'info' });
    }
  }, [analysisDeleteSummaryRichText, analysisId, dispatch]);

  const setShowSummaryInDashboard = useCallback(
    async (showInDashboard: boolean) => {
      const resp = await analysisSetShowSummaryInDashboard({
        variables: {
          input: {
            analysisId,
            showInDashboard,
          },
        },
      });
      if (
        printGraphqlErrors(resp.data?.analysisSetShowSummaryInDashboard.errors)
      ) {
        return;
      }
      dispatch(
        updateAnalysisSummaryShowInDashboard({
          analysisId,
          showInDashboard,
        }),
      );
    },
    [analysisId, analysisSetShowSummaryInDashboard, dispatch],
  );

  return {
    saveSummaryRichText,
    setShowSummaryInDashboard,
    deleteSummaryRichText,
    saving: savingRichText || savingShowInDashboard || deletingRichText,
  };
};

export default useAnalysisSaveSummaryRichText;
